import type {AccountVisibilityTier} from "@buildwithflux/accounts-schema";
import type {AvatarAdapter, AvatarData} from "@buildwithflux/avatar-logic";
import {isPictureFieldObject} from "@buildwithflux/avatar-logic";
import {isEnterpriseUid, isOrganizationUid, userUidForms} from "@buildwithflux/identifiers-schema";
import {useEffect, useState} from "react";

/**
 * Public client-side Firebase config for the Flux app (graviton-mvp).
 * Mirrors packages/constants/src/firebaseProjects.ts — not secrets.
 */
const FIREBASE_CONFIG = {
    projectId: "graviton-mvp",
    appId: "1:706308333141:web:9c78f0286da170472b3edc",
    apiKey: "AIzaSyAXNgauDwq62Ev-SVuub9VrKrGn2eCE6KE",
    authDomain: "auth.flux.ai",
    databaseURL: "https://graviton-mvp.firebaseio.com",
    storageBucket: "graviton-mvp.appspot.com",
    messagingSenderId: "706308333141",
    measurementId: "G-SZ17792FZ5",
} as const;

/**
 * View collection path for account profile data by UID.
 *
 * The tier is viewer-dependent (matches AccountViewRepository semantics):
 * - `public`: anonymous viewers / crawlers — only sees fields the owner made public.
 * - `users`: authenticated Flux viewers — also sees fields restricted to logged-in users.
 *
 * Org/enterprise UIDs already carry their own (`org_`/`ent_`) prefix and are the
 * canonical key the view collection uses; only bare user UIDs get the `usr_`
 * prefix. Prefixing an org UID would mint a bogus `usr_org_…` key and miss
 * the doc (falling back to letter initials).
 */
export function viewDocPath(uid: string, tier: AccountVisibilityTier): string {
    const normalizedUid = isOrganizationUid(uid) || isEnterpriseUid(uid) ? uid : userUidForms(uid)[1];
    return `view/account/${tier}/profile/by-uid/${normalizedUid}`;
}

interface AvatarProfileSnapshot {
    exists(): boolean;
    data():
        | {
              readonly picture?: unknown;
              readonly name?: unknown;
              readonly handle?: unknown;
          }
        | undefined;
}

type AvatarSnapshotSubscriber<TReference> = (
    reference: TReference,
    onNext: (snapshot: AvatarProfileSnapshot) => void,
    onError: (cause: unknown) => void,
) => () => void;

export function subscribeToAvatarSnapshot<TReference>(
    subscribe: AvatarSnapshotSubscriber<TReference>,
    reference: TReference,
    isCancelled: () => boolean,
    onData: (data: AvatarData) => void,
): () => void {
    return subscribe(
        reference,
        (snapshot) => {
            if (isCancelled()) return;

            if (!snapshot.exists()) {
                onData({picture: null, displayName: null});
                return;
            }

            const snapshotData = snapshot.data() ?? {};
            const displayName = snapshotData.name ?? snapshotData.handle;
            onData({
                picture: isPictureFieldObject(snapshotData.picture) ? snapshotData.picture : null,
                displayName: typeof displayName === "string" ? displayName : null,
            });
        },
        () => {
            if (!isCancelled()) {
                onData({picture: null, displayName: null});
            }
        },
    );
}

function useFirestoreAvatarData(uid: string | null): AvatarData | undefined {
    const [data, setData] = useState<AvatarData | undefined>(undefined);

    useEffect(() => {
        if (!uid) {
            setData(undefined);
            return;
        }

        const currentUid = uid;
        let unsubscribeDoc: (() => void) | undefined;
        let unsubscribeAuth: (() => void) | undefined;
        let cancelled = false;

        async function subscribeToAvatarData() {
            try {
                const [firebaseApp, firebaseAuth, firebaseFirestore] = await Promise.all([
                    import("firebase/app"),
                    import("firebase/auth"),
                    import("firebase/firestore"),
                ]);

                if (cancelled) return;

                const existingApps = firebaseApp.getApps();
                const app =
                    existingApps.length > 0
                        ? existingApps[0]!
                        : firebaseApp.initializeApp(FIREBASE_CONFIG, FIREBASE_CONFIG.projectId);

                const db = firebaseFirestore.getFirestore(app);
                const auth = firebaseAuth.getAuth(app);

                // The tier is auth-aware: re-subscribe to the right view doc whenever
                // the viewer's auth state changes (anon -> public, signed-in -> users).
                unsubscribeAuth = firebaseAuth.onAuthStateChanged(auth, (firebaseUser) => {
                    if (cancelled) return;

                    unsubscribeDoc?.();

                    const tier: AccountVisibilityTier = firebaseUser ? "users" : "public";
                    const docRef = firebaseFirestore.doc(db, viewDocPath(currentUid, tier));

                    unsubscribeDoc = subscribeToAvatarSnapshot(
                        (reference, onNext, onError) =>
                            firebaseFirestore.onSnapshot(
                                reference,
                                (snapshot) => onNext(snapshot),
                                (error) => onError(error),
                            ),
                        docRef,
                        () => cancelled,
                        setData,
                    );
                });
            } catch {
                if (!cancelled) {
                    setData({picture: null, displayName: null});
                }
            }
        }

        void subscribeToAvatarData();

        return () => {
            cancelled = true;
            unsubscribeDoc?.();
            unsubscribeAuth?.();
        };
    }, [uid]);

    return data;
}

/**
 * Firestore-backed {@link AvatarAdapter} shared by the standalone apps
 * (site-search, docs-site, internal-app). Reads the account view collection
 * directly via the modular Firebase SDK, picking the tier from the viewer's
 * auth state so authenticated viewers see pictures restricted to the `users` tier.
 */
export const firestoreAvatarAdapter: AvatarAdapter = {
    useAvatarData: useFirestoreAvatarData,
};
